'use client';

/**
 * DayHeader — the chapter heading for one day of the trip, inside StopList:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   ▌ Day 1  Sat 17 Oct          [photo of tonight's town]  27°  ☀ 7:41 pm │
 *   └──────────────────────────────────────────────────────────────┘
 *   Melbourne → Lakes Entrance
 *   319 km · 3h 50m · arrive 12:50 pm
 *   [◷ Long day: over 6h driving]  [Split this day]
 *
 * The band is the day's colour (the same as its route and pins on the map)
 * with tonight's town laid over it once its photo loads, so each day has a
 * sense of place. A left edge in the day's colour stays visible over the
 * photo. Sunset shows when the trip has a date; weather only when it should
 * change a plan.
 *
 * The header owns the day's totals. The legs below add per-leg figures only
 * on days with more than one drive, so a figure is never said twice.
 */
import React, { useState } from 'react';
import { formatClock, formatShortDate } from '../../utils/tripFormat';
import { formatDrive, formatKm } from './geo';
import { weatherWarning, type DayForecastSummary } from '../../utils/tripWeather';
import { ArrowRightIcon, BedIcon, LongDayIcon, SunsetIcon, ViewpointIcon, WarningWeatherIcon, WeatherIcon } from './dayIcons';

export type { DayForecastSummary } from '../../utils/tripWeather';

export interface DayHeaderProps {
  /** 1-based day number on the calendar (skips past rest days). */
  dayNumber: number;
  /** YYYY-MM-DD for this day, when the trip has a departure date. */
  date?: string | null;
  fromName: string;
  toName: string;
  driveMinutes?: number;
  distanceKm?: number;
  arriveMinutes?: number;
  /** Any of the day's legs is an estimate (no road route yet). */
  estimated?: boolean;
  forecast?: DayForecastSummary | null;
  /** Drive time (minutes) above which the day gets a "Long day" chip. */
  longDayOverMinutes?: number;
  /** The fix offered next to "Long day" ("Split this day"). */
  onSplitDay?: () => void;
  /** What the split does, for screen readers and the tooltip. */
  splitHint?: string;
  /** The day's colour (the same as its route and pins on the map). */
  color?: string;
  /** Tonight's town's photo (the stop photo, hero-sized). */
  photo?: string | null;
  /** Tonight's town, for the sunset tooltip. */
  placeName?: string;
  /** "7:41 pm" at tonight's town. */
  sunset?: string | null;
  /** id for the band's title (aria-labelledby, DayNav's jump target). */
  titleId?: string;
}

/** True when the day's driving runs past the budget. */
export function isLongDay(driveMinutes: number | undefined, overMinutes: number | undefined): boolean {
  return typeof driveMinutes === 'number' && typeof overMinutes === 'number' && overMinutes > 0 && driveMinutes > overMinutes;
}

export function longDayLabel(overMinutes: number): string {
  return `Long day: over ${formatDrive(overMinutes)} driving`;
}

/** Plain-text summary of a day (tests, print, screen-reader summaries). */
export function dayHeaderText(p: DayHeaderProps): string {
  const date = formatShortDate(p.date);
  const figures = [formatKm(p.distanceKm), formatDrive(p.driveMinutes)].filter(Boolean).join(' · ');
  const arrive = formatClock(p.arriveMinutes);
  return [`Day ${p.dayNumber}`, date, `${p.fromName} → ${p.toName}`, figures || null, arrive ? `arrive ${arrive}` : null]
    .filter(Boolean)
    .join(' · ');
}

/** Weather chips: storms and heat are the dangers; rain, wind and cold inform. */
const WEATHER_TONE: Record<string, string> = {
  storm: 'bg-tp-danger-soft text-tp-danger ring-tp-danger/25',
  heat: 'bg-tp-danger-soft text-tp-danger ring-tp-danger/25',
  wind: 'bg-tp-surface text-tp-ink ring-tp-line-strong',
  rain: 'bg-tp-info-soft text-tp-info ring-tp-info/25',
  cold: 'bg-tp-info-soft text-tp-info ring-tp-info/25',
};

const chipBase =
  'inline-flex min-h-[28px] shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-tp-sm font-semibold ring-1 ring-inset';

/** Secondary button used in day bodies ("Split this day", "Explore nearby"). */
export const dayActionBtn =
  'inline-flex h-9 min-h-0 shrink-0 items-center gap-1.5 rounded-tp border-0 bg-white px-3 text-tp-sm font-semibold ring-1 ring-inset ring-tp-line-strong transition-colors duration-150 ease-tp-out focus:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45';

/** Faint contour lines: the band's texture before (or without) a photo. */
const Contours: React.FC = () => (
  <svg aria-hidden="true" className="absolute inset-0 h-full w-full opacity-[.14]" preserveAspectRatio="none" viewBox="0 0 400 72">
    <g fill="none" stroke="#fff" strokeWidth="1.2">
      <path d="M-10 60c60-18 110 8 170-10s120-30 250-6" />
      <path d="M-10 44c70-16 120 6 180-12s130-24 240-4" />
      <path d="M-10 28c80-14 130 4 190-12s120-18 230 0" />
      <path d="M-10 12c90-12 140 2 200-10s110-12 220 2" />
    </g>
  </svg>
);

/** The day band: colour, then tonight's town, the title, weather and sunset. */
export const DayBand: React.FC<{
  dayNumber: number;
  date?: string | null;
  color: string;
  photo?: string | null;
  placeName?: string;
  sunset?: string | null;
  forecast?: DayForecastSummary | null;
  /** "Rest day" tag after the date. */
  tag?: string;
  titleId?: string;
}> = ({ dayNumber, date, color, photo, placeName, sunset, forecast, tag, titleId }) => {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const loaded = !!photo && loadedSrc === photo;
  const dateLabel = formatShortDate(date);
  const temp = forecast && typeof forecast.max === 'number' ? Math.round(forecast.max) : null;
  return (
    <div
      data-testid="day-band"
      className="relative flex h-14 items-center overflow-hidden rounded-tp sm:h-16"
      style={{ background: color }}
    >
      <Contours />
      {photo && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={photo}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          ref={(img) => {
            if (img && img.complete && img.naturalWidth > 0 && loadedSrc !== photo) setLoadedSrc(photo);
          }}
          onLoad={() => setLoadedSrc(photo)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 motion-reduce:transition-none ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
      {/* Scrim: white text stays readable on a bright sky. */}
      <span
        aria-hidden="true"
        className={`absolute inset-0 transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: 'linear-gradient(90deg, rgba(22,35,58,.66) 0%, rgba(22,35,58,.34) 55%, rgba(22,35,58,.2) 100%)' }}
      />
      {/* The day's colour stays on the left edge over the photo. */}
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[6px]" style={{ background: color }} />
      <h3
        id={titleId}
        tabIndex={-1}
        className="relative m-0 flex min-w-0 flex-1 items-baseline gap-2 pl-4 pr-2 text-white outline-none [text-shadow:0_1px_2px_rgba(0,0,0,.35)]"
      >
        <span className="shrink-0 text-tp-xl font-semibold">Day {dayNumber}</span>
        {dateLabel && <span className="truncate text-tp-base font-medium text-white/90">{dateLabel}</span>}
        {tag && (
          <span className="shrink-0 self-center rounded-full bg-white/20 px-2 py-0.5 text-tp-xs font-semibold ring-1 ring-inset ring-white/30 [text-shadow:none]">
            {tag}
          </span>
        )}
      </h3>
      <div className="relative flex shrink-0 items-center gap-1.5 pr-2">
        {temp !== null && forecast && (
          <span
            className="hidden h-7 items-center gap-1 rounded-full bg-black/30 px-2 text-tp-sm font-semibold tabular-nums text-white backdrop-blur-[2px] min-[400px]:inline-flex"
            title={`${forecast.condition}, top of ${temp}°`}
            data-testid="day-temp"
          >
            <WeatherIcon condition={forecast.condition} className="h-4 w-4" />
            {temp}°
          </span>
        )}
        {sunset && (
          <span
            data-testid="day-sunset"
            className="inline-flex h-7 items-center gap-1 rounded-full bg-black/30 px-2 text-tp-sm font-semibold tabular-nums text-white backdrop-blur-[2px]"
            title={`Sunset ${sunset}${placeName ? ` in ${placeName.split(',')[0]}` : ''}`}
          >
            <SunsetIcon className="h-4 w-4" />
            <span className="sr-only">Sunset </span>
            {sunset}
          </span>
        )}
      </div>
    </div>
  );
};

const DayHeader: React.FC<DayHeaderProps> = (props) => {
  const {
    dayNumber,
    date,
    fromName,
    toName,
    driveMinutes,
    distanceKm,
    arriveMinutes,
    estimated = false,
    forecast,
    longDayOverMinutes,
    onSplitDay,
    splitHint,
    color = '#1558b0',
    photo,
    placeName,
    sunset,
    titleId,
  } = props;
  const time = formatDrive(driveMinutes);
  const km = formatKm(distanceKm);
  const arrive = formatClock(arriveMinutes);
  const warning = weatherWarning(forecast);
  const long = isLongDay(driveMinutes, longDayOverMinutes);

  return (
    <div data-testid="day-header" className="pb-1">
      <DayBand
        dayNumber={dayNumber}
        date={date}
        color={color}
        photo={photo}
        placeName={placeName ?? toName}
        sunset={sunset}
        forecast={forecast}
        titleId={titleId}
      />
      <div className="px-1 pt-2">
        <p className="m-0 flex min-w-0 items-center gap-1.5 text-tp-lg font-semibold text-tp-ink">
          <span className="truncate">{fromName}</span>
          <ArrowRightIcon className="h-4 w-4 shrink-0 text-tp-faint" />
          <span className="sr-only">to</span>
          <span className="truncate">{toName}</span>
        </p>
        {(time || km || arrive) && (
          <p className="m-0 mt-0.5 flex flex-wrap items-center gap-x-1.5 text-tp-base tabular-nums text-tp-muted" data-testid="day-figures">
            <span className="whitespace-nowrap">
              {km && <span className="font-semibold text-tp-ink">{km}</span>}
              {time && km && <span aria-hidden="true" className="text-tp-faint"> · </span>}
              {time && <span className="font-semibold text-tp-ink">{time}</span>}
            </span>
            {(time || km) && arrive && <span aria-hidden="true" className="text-tp-faint"> · </span>}
            {arrive && <span className="whitespace-nowrap">arrive {arrive}</span>}
            {estimated && (time || km) && (
              <span
                className="ml-1 whitespace-nowrap rounded-full bg-tp-surface px-2 text-tp-xs font-medium leading-5 text-tp-muted ring-1 ring-inset ring-tp-line"
                title="Straight from the map until the road route loads"
              >
                approx.
              </span>
            )}
          </p>
        )}
        {(long || warning) && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {long && longDayOverMinutes && (
              <>
                <span data-testid="long-day" className={`${chipBase} bg-tp-warn-soft text-tp-warn ring-tp-warn-line`}>
                  <LongDayIcon className="h-3.5 w-3.5" />
                  {longDayLabel(longDayOverMinutes)}
                </span>
                {onSplitDay && (
                  <button
                    type="button"
                    onClick={onSplitDay}
                    data-testid="split-day"
                    data-no-drag
                    title={splitHint}
                    className={`${dayActionBtn} h-8 text-tp-brand hover:bg-tp-brand-soft`}
                  >
                    <BedIcon className="h-4 w-4" />
                    Split this day
                    {splitHint && <span className="sr-only">: {splitHint}</span>}
                  </button>
                )}
              </>
            )}
            {warning && (
              <span className={`${chipBase} ${WEATHER_TONE[warning.kind] ?? WEATHER_TONE.rain}`} title={warning.detail}>
                <WarningWeatherIcon kind={warning.kind} className="h-3.5 w-3.5" />
                {warning.label}
                <span className="sr-only">. {warning.detail}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/** A whole day at a multi-night stop: where you are, and the two things
 *  people do with a spare day. Leaving early drops a night, so it asks first. */
export const RestDayHeader: React.FC<{
  dayNumber: number;
  date?: string | null;
  placeName: string;
  photo?: string | null;
  color?: string;
  sunset?: string | null;
  titleId?: string;
  /** "Explore nearby" (opens the map around the stop). */
  onExplore?: () => void;
  /** "Leave a day early" (one night fewer there). */
  onDriveOn?: () => void;
}> = ({ dayNumber, date, placeName, photo, color = '#1558b0', sunset, titleId, onExplore, onDriveOn }) => {
  const [confirming, setConfirming] = useState(false);
  const short = placeName.split(',')[0];
  return (
    <div data-testid="rest-day-header" className="pb-1">
      <DayBand dayNumber={dayNumber} date={date} color={color} photo={photo} placeName={placeName} sunset={sunset} tag="Rest day" titleId={titleId} />
      <div className="px-1 pt-2">
        <p className="m-0 text-tp-lg font-semibold text-tp-ink">Rest day in {short}</p>
        <p className="m-0 text-tp-base text-tp-muted">No driving. The camp stays put.</p>
        {(onExplore || onDriveOn) && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2" aria-live="polite" data-no-drag>
            {confirming && onDriveOn ? (
              <div
                role="group"
                aria-label={`Leave ${short} a day early`}
                className="flex w-full flex-wrap items-center gap-2 rounded-tp bg-tp-surface p-2 pl-3 ring-1 ring-inset ring-tp-line"
              >
                <p className="m-0 min-w-0 flex-1 text-tp-base text-tp-ink">Drop a night in {short}? The days after move up one.</p>
                <button
                  type="button"
                  autoFocus
                  onClick={() => {
                    setConfirming(false);
                    onDriveOn();
                  }}
                  className="inline-flex h-9 min-h-0 items-center rounded-tp border-0 bg-tp-ink px-3 text-tp-sm font-semibold text-white transition-colors hover:bg-tp-ink/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 focus-visible:ring-offset-2"
                >
                  Drop the night
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="inline-flex h-9 min-h-0 items-center rounded-tp border-0 bg-transparent px-3 text-tp-sm font-semibold text-tp-ink transition-colors hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                >
                  Keep it
                </button>
              </div>
            ) : (
              <>
                {onExplore && (
                  <button type="button" onClick={onExplore} className={`${dayActionBtn} text-tp-brand hover:bg-tp-brand-soft`}>
                    <ViewpointIcon className="h-4 w-4" />
                    Explore nearby
                  </button>
                )}
                {onDriveOn && (
                  <button type="button" onClick={() => setConfirming(true)} className={`${dayActionBtn} text-tp-ink hover:bg-tp-surface`}>
                    <ArrowRightIcon className="h-4 w-4 text-tp-muted" />
                    Leave a day early
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DayHeader;
